// DEVE restare il primo import: rende `process` disponibile nel browser per le
// letture `process.env.PUBLIC_*` che Bun non ha inlineato (variabile assente
// al build). Vedi polyfills/process.ts.
import './polyfills/process';
import './config/early-error-handler';
import './config/sentry';
import './theme/fonts.css';
import './config/i18n';
import 'reflect-metadata';
import { CLERK_PUBLISHABLE_KEY, IS_LOCAL, MISSING_CONFIG, SENTRY_DSN } from './config/env';

// Bootstrap: registra tutti i servizi nel container DI
// IMPORTANTE: deve essere importato PRIMA di usare il container
import './services';

import * as Sentry from '@sentry/react';
import { Provider as DiProvider } from 'inversify-react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { Workbox } from 'workbox-window';
import App from './App';
import { ConfigurationMissingScreen } from './components/configuration-missing-screen';
import { ErrorBoundary } from './components/error-boundary';
import { installClerkErrorHandler } from './config/clerk-error-handler';
import { installClerkFapiMonitor } from './config/clerk-fapi-monitor';
import { ClerkLocalizationProvider } from './config/clerk-localization-provider';
import { dependencyContainer } from './config/di-container';
import { didBootstrapFail, flushBootstrapErrorsToSentry } from './config/early-error-handler';
import { captureAttribution } from './services/analytics/attribution';
import { installGtm } from './services/analytics/install-gtm';
import {
  installChunkErrorDevWarner,
  installChunkErrorReloader,
} from './services/pwa/chunk-error-reloader';
import { requestGatedReload } from './services/pwa/reload-gate';

// Pin clerk-js to a specific version. Without an explicit pin the SDK resolves
// to `@clerk/clerk-js@<major>` via CDN, which on devices with stale cached JS
// can trigger "Response: unknown not supported yet" when server-side flow enums
// drift past what the cached bundle knows. Pinning bumps the URL → cache miss.
const CLERK_JS_VERSION = '6.10.1';

// Ignora errori delle estensioni del browser che causano "message channel closed"
window.addEventListener('unhandledrejection', function (event) {
  const reason = event.reason;
  if (
    reason instanceof Error &&
    (reason.message.includes('message channel closed') ||
      reason.message.includes('A listener indicated an asynchronous response by returning true'))
  ) {
    console.warn('[Prospero] Ignored extension-related error:', reason.message);
    event.preventDefault();
  }
});

if (IS_LOCAL) {
  installChunkErrorDevWarner();
}

// NB: do NOT add a non-passive `wheel` listener on document. In Safari a
// non-passive wheel listener combined with `overscroll-behavior: none`
// (set on html/body in index.html) silently kills wheel/trackpad scrolling
// of the whole document — programmatic scrollTo still works, so it only shows
// up as "the page won't scroll with the trackpad" on Safari desktop. Pinch
// zoom is handled by the Safari gesture events below.

// Safari-specific gesture events (pinch zoom)
document.addEventListener('gesturestart', (gestureEvent) => {
  gestureEvent.preventDefault();
});
document.addEventListener('gesturechange', (gestureEvent) => {
  gestureEvent.preventDefault();
});
document.addEventListener('gestureend', (gestureEvent) => {
  gestureEvent.preventDefault();
});

// Capture first-touch acquisition attribution before any client-side
// navigation strips utm params / referrer from the entry URL.
captureAttribution();

// Register the GTM container id + replay saved anonymous consent, letting the
// inline head loader start gtm.js (see config/env.GTM_ID for the id sources).
installGtm();

flushBootstrapErrorsToSentry(Sentry);

installClerkErrorHandler();

// Clerk's prebuilt components swallow 422 validation errors (shown inline in
// the form), so they never reach Sentry. Watch the Clerk FAPI host explicitly.
installClerkFapiMonitor({ publishableKey: CLERK_PUBLISHABLE_KEY });

function mountApp(): void {
  const rootEl = document.getElementById('root');
  if (!rootEl) {
    return;
  }
  if (didBootstrapFail()) {
    return;
  }

  const root = ReactDOM.createRoot(rootEl, {
    onUncaughtError: Sentry.reactErrorHandler(),
    onCaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn('Caught error', error, errorInfo.componentStack);
    }),
  });

  if (MISSING_CONFIG) {
    if (SENTRY_DSN) {
      Sentry.captureMessage(`Prospero config missing: ${MISSING_CONFIG.reason}`, 'fatal');
    }
    root.render(
      <React.StrictMode>
        <ConfigurationMissingScreen
          reason={MISSING_CONFIG.reason}
          detail={MISSING_CONFIG.detail}
          envName={MISSING_CONFIG.envName}
        />
      </React.StrictMode>,
    );
    (window as Window & { __prosperoReactMounted?: boolean }).__prosperoReactMounted = true;
  } else {
    root.render(
      <React.StrictMode>
        <ErrorBoundary level="root">
          <ClerkLocalizationProvider
            publishableKey={CLERK_PUBLISHABLE_KEY}
            clerkJsVersion={CLERK_JS_VERSION}
          >
            <DiProvider container={dependencyContainer}>
              <App />
            </DiProvider>
          </ClerkLocalizationProvider>
        </ErrorBoundary>
      </React.StrictMode>,
    );
    (window as Window & { __prosperoReactMounted?: boolean }).__prosperoReactMounted = true;
  }
}

mountApp();

if (!IS_LOCAL && !MISSING_CONFIG) {
  installChunkErrorReloader();
}

// Register service worker for PWA precaching (production builds only).
// skipWaiting: true + clientsClaim: true in generate-sw.mjs ensure the new SW
// activates immediately and claims all clients — no "waiting" state, no user
// prompt needed, no blank blue page from stale chunk hashes.
if (!IS_LOCAL && !MISSING_CONFIG && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    const wb = new Workbox('/sw.js', { updateViaCache: 'none' });

    wb.addEventListener('controlling', (event) => {
      // With clientsClaim the SW takes control on its very FIRST install too:
      // reloading there restarts the page mid-bootstrap (visible flicker) for
      // zero benefit — the page was just served fresh from the network. Only
      // reload when an UPDATED SW takes over (stale precached chunk hashes).
      // The reload is gated: never restart the page while the operator has a
      // form dialog open or a mutation in flight.
      if (!event.isUpdate) {
        return;
      }
      requestGatedReload();
    });

    wb.register().catch((registrationError) => {
      console.warn('[Prospero] SW registration failed:', registrationError);
    });
  });
}
